import { useLayoutEffect, useState } from 'react';

const RESIZE_DELAY = 200;

const fitCount = (size, itemSize, gap) => Math.floor((size + gap) / (itemSize + gap));

// Columns and rows of fixed-size items that fit into the visible part of the page without scrolling.
// Without itemWidth the list is a single column (only rows are counted).
// Returns null while disabled (mobile layout) or before the first measurement.
const useGridLayout = ({
  gridRef, paginationRef, scrollableContentRef, isEnabled, itemHeight, itemWidth, minColumns = 1, minRows = 1,
}) => {
  const [layout, setLayout] = useState(null);

  useLayoutEffect(() => {
    if (!isEnabled) {
      setLayout(null);
      return undefined;
    }

    const measure = () => {
      const grid = gridRef.current;
      const scrollable = scrollableContentRef?.current;
      if (!grid || !scrollable) return;

      // Wrapper: the scrollable content holds the page container (min-height excludes the footer) and the footer.
      const main = scrollable.firstElementChild || scrollable;
      const mainHeight = parseFloat(getComputedStyle(main).minHeight) || scrollable.clientHeight;
      const gridStyle = getComputedStyle(grid);
      const columnGap = parseFloat(gridStyle.columnGap) || 0;
      const rowGap = parseFloat(gridStyle.rowGap) || 0;

      const width = grid.clientWidth - parseFloat(gridStyle.paddingLeft) - parseFloat(gridStyle.paddingRight);
      const gridTop = grid.getBoundingClientRect().top - main.getBoundingClientRect().top;
      const height = mainHeight
        - gridTop
        - parseFloat(gridStyle.marginBottom)
        - (paginationRef.current?.offsetHeight || 0);

      const columns = itemWidth ? Math.max(minColumns, fitCount(width, itemWidth, columnGap)) : 1;
      const rows = Math.max(minRows, fitCount(height, itemHeight, rowGap));

      setLayout((prev) => (prev?.columns === columns && prev?.rows === rows ? prev : { columns, rows }));
    };

    measure();

    let timeout = null;
    const onResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(measure, RESIZE_DELAY);
    };

    // The grid width also changes when the scrollbar appears or disappears, not only with the window.
    const observer = new ResizeObserver(onResize);
    if (gridRef.current) observer.observe(gridRef.current);
    if (scrollableContentRef?.current) observer.observe(scrollableContentRef.current);
    window.addEventListener('resize', onResize);

    return () => {
      clearTimeout(timeout);
      observer.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [isEnabled, scrollableContentRef]);

  return layout;
};

export default useGridLayout;
